import React, {Component} from "react"
import {Row} from "react-bootstrap"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {H6, Body} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import Flex from "@irati/ui/src/Flex"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {IconTypes} from "@irati/ui/src/Icon"
import teamsService from "@irati/identity/services/teams"
import {Select} from "@irati/ui/src/FormItem"
import Loading from "@irati/ui/src/Loading"
import FilterChipToggle from "@irati/ui/src/FilterChipToggle"
import {OrganisationalSummaryCategory, OrganisationalSummaryDuration, PaymentCurrencyType} from "@irati/types"
import adminTracking from "@irati-tracking/admin/admin"
import mainTheme from "@irati/ui/src/global/theme"
import Divider from "@irati/ui/src/Divider"

import Region from "../Reporting/Region"
import EmptyData from "../Reporting/EmptyData/EmptyData"
import SummaryList from "./SummaryList"
import TrendRates from "./TrendRates"
import Categories from "./Categories"
import Download from "./Download"
import buildSummary from "./buildSummary"
import {Header} from "./styled"

const ALL_TEAMS = "ALL"

interface Option {
  value: string | OrganisationalSummaryCategory | OrganisationalSummaryDuration,
  label: string
}

interface Filters {
  businessUnitIds?: string[],
  businessUnitId: string,
  teamId?: string,
  currency?: string
}

interface Summary {
  title: string,
  description: string,
  value: number,
  isTrendRate?: boolean,
  linksToFAQ?: boolean
}

interface Props extends InjectIntl {}

interface State {
  load: Promise<any>|null,
  buttonLoad: Promise<any>|null,
  summary: Summary[],
  teamOptions: Option[],
  timeRange: OrganisationalSummaryDuration,
  timeRangeOptions: Option[],
  filters: Filters,
  category: OrganisationalSummaryCategory,
  showNoData: boolean,
  showCategories: boolean
}

class OrganisationalSummary extends Component<Props, State> {
  state: State = {
    load: null,
    buttonLoad: null,
    summary: [],
    teamOptions: [],
    timeRange: OrganisationalSummaryDuration.SummaryLast_30Days,
    timeRangeOptions: [],
    filters: {
      businessUnitIds: [],
      businessUnitId: ALL_TEAMS,
      teamId: ALL_TEAMS,
      currency: PaymentCurrencyType.Gbp
    },
    category: OrganisationalSummaryCategory.Main,
    showNoData: false,
    showCategories: true
  }

  componentDidMount() {
    const {formatMessage} = this.props

    const load = adminReportingService.getOrganisationalSummary({businessId: authentication.business().uuid}).then(async(res) => {
      const summary = buildSummary(res, OrganisationalSummaryCategory.Main, PaymentCurrencyType.Gbp)
      if (summary?.length) {
        this.setState({summary})
      }
      else {
        this.setState({showNoData: true})
      }

      const teams = await teamsService.list(authentication.business().uuid)
      const baseTeamOptions = teams.map((team) => ({label: team.teamName, value: team.uuid}))
      const teamOptions = [
        {label: formatMessage({id: "admin.reporting.popularPerks.allTeams"}), value: ALL_TEAMS},
        ...baseTeamOptions
      ]
      const timeRangeOptions = [
        {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last30Days"}),
          value: OrganisationalSummaryDuration.SummaryLast_30Days},
        {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last90Days"}),
          value: OrganisationalSummaryDuration.SummaryLast_90Days},
        {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last6Months"}),
          value: OrganisationalSummaryDuration.SummaryLast_6Months},
        {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last12Months"}),
          value: OrganisationalSummaryDuration.SummaryLast_12Months}
      ]
      this.setState({teamOptions, timeRangeOptions, timeRange: timeRangeOptions[0].value})
    }).catch(() => this.setState({showNoData: true}))
    this.setState({load, buttonLoad: load})
  }

  buildTeams = () => {
    const {filters} = this.state
    if (filters.businessUnitId !== ALL_TEAMS) {
      return [filters.businessUnitId]
    }
    if (filters.teamId !== ALL_TEAMS) {
      return [filters.teamId]
    }
  }

  getOrganisationSummary = () => {
    const {timeRange, category, filters} = this.state
    const variables = {
      businessId: authentication.business().uuid,
      category,
      duration: timeRange,
      teams: this.buildTeams()
    }
    const load = adminReportingService.getOrganisationalSummary(variables).then(async(res) => {
      this.setState({summary: buildSummary(res, category, filters.currency)})
    }).catch(() => this.setState({showNoData: true}))
    this.setState({load, showNoData: false})
  }

  getTrendRates = () => {
    return this.state.summary.filter((dataPoint) => dataPoint.isTrendRate)
  }

  getSummaryList = () => {
    return this.state.summary.filter((dataPoint) => !dataPoint.isTrendRate)
  }

  handleUpdateBusinessUnit = (businessUnitId: string) => {
    adminTracking.analyticsEngagementSetCountry()
    this.setState((prevState: State) => ({
      filters: {...prevState.filters, businessUnitId}
    }), this.getOrganisationSummary)
  }

  handleUpdateTeam = (teamId: string) => {
    adminTracking.analyticsEngagementSetTeam()
    this.setState((prevState: State) => ({filters: {...prevState.filters, teamId}
    }), this.getOrganisationSummary)
  }

  handleUpdateCurrency = (currency: string) => {
    adminTracking.analyticsEngagementSetCurrency()
    this.setState((prevState: State) => ({filters:
      {...prevState.filters, currency}
    }), this.getOrganisationSummary)
  }

  handleSetTimeRangeUpdate = (timeRange: OrganisationalSummaryDuration) => {
    adminTracking.analyticsEngagementSetDate()
    this.setState({timeRange}, this.getOrganisationSummary)
  }

  handleUpdateCategory = (category: OrganisationalSummaryCategory) => {
    if (category === OrganisationalSummaryCategory.Main) {
      adminTracking.analyticsEngagementClickAllFilter()
    }
    if (category === OrganisationalSummaryCategory.Perks) {
      adminTracking.analyticsEngagementClickPerkFilter()
    }
    if (category === OrganisationalSummaryCategory.FlexiPerks) {
      adminTracking.analyticsEngagementClickFlexiPerkFilter()
    }
    if (category === OrganisationalSummaryCategory.Celebrations) {
      adminTracking.analyticsEngagementClickCelebrationsFilter()
    }
    if (category === OrganisationalSummaryCategory.Wellness) {
      adminTracking.analyticsEngagementClickWellnessFilter()
    }
    this.setState({category}, this.getOrganisationSummary)
  }

  handleToggleCategories = () => {
    adminTracking.analyticsEngagementClickFilter()
    this.setState((prevState: State) => {
      return {showCategories: !prevState.showCategories}
    })
  }

  render() {
    const {
      teamOptions,
      timeRangeOptions,
      timeRange,
      filters,
      load,
      buttonLoad,
      category,
      showCategories,
      summary,
      showNoData} = this.state

    return <Card marginBottom={8}>
      <Flex justifyContent="space-between" marginBottom={4}>
        <Header>
          <H6 marginBottom={1}>
            <FormattedMessage id="admin.reporting.orgSummary.title" />
          </H6>
          <Body small>
            <FormattedMessage id="admin.reporting.orgSummary.desc" />
          </Body>
        </Header>
        <Loading load={buttonLoad}>
          <Flex alignItems="center" gap={mainTheme.spacing.oneAndHalf} justifyContent="flex-end">
            <Region
              value={filters.businessUnitId}
              onChange={this.handleUpdateBusinessUnit} />
            {authentication.apps("admin.reportingBeta") &&
              <Row>
                <Select
                  id="adminSection_organisationalSummary_teams"
                  isDisabled={filters.businessUnitId !== ALL_TEAMS}
                  options={teamOptions}
                  value={filters.teamId}
                  onChange={this.handleUpdateTeam} />
              </Row>
            }
            <Row>
              <Select
                id="adminSection_organisationalSummary_timePeriod"
                name="timeRangeMostPopularPerks"
                options={timeRangeOptions}
                value={timeRange}
                onChange={this.handleSetTimeRangeUpdate} />
            </Row>
            <Divider />
            <FilterChipToggle isToggled={showCategories} icon={IconTypes.filter3} onClick={this.handleToggleCategories} id="expandButton" />
          </Flex>
          {showCategories && <Categories
            onChange={this.handleUpdateCategory}
            selectedCategory={category}
            currency={filters.currency}
            onCurrencyChange={this.handleUpdateCurrency} />}
        </Loading>
      </Flex>
      <Loading load={load}>
        {showNoData
          ? <EmptyData />
          : <>
            <TrendRates
              trendRates={this.getTrendRates()}
              hideEngagementRate={
                timeRange !== OrganisationalSummaryDuration.SummaryLast_30Days || filters?.businessUnitId !== ALL_TEAMS
              } />
            <SummaryList
              hideVisits={filters?.businessUnitId === ALL_TEAMS
                && category === OrganisationalSummaryCategory.Main
                && !authentication.apps("admin.reportingBeta")}
              summaries={this.getSummaryList()} />
          </>
        }
      </Loading>
      <Download summary={summary} />
    </Card>
  }
}

export default injectIntl(OrganisationalSummary)
